// =============================================================================
// PLANT LAYOUT — TYPE DEFINITIONS
// =============================================================================

export type PierType  = 'HAP' | 'HMP' | 'SAP' | 'SMP' | 'SAP_EDGE' | 'SAP_END'
export type PierColor = 'BLANCO' | 'AZUL' | 'GRIS' | 'AMARILLO' | 'VERDE'
export type TrackerStatus = 'NO_INICIADA' | 'EN_CURSO' | 'FINALIZADA' | 'BLOQUEADA'
export type TrackerStrings = '1S' | '2S' | '1.5S'

// Pile installation step states
export type PileStepState = 'NO_INICIADA' | 'EN_CURSO' | 'FINALIZADA'

// ---------------------------------------------------------------------------
// Raw parsed pile from CSV
// ---------------------------------------------------------------------------
export interface PileData {
  codigoHinca:   string
  codigoTracker: string
  x:             number     // UTM Easting  (EPSG:25830)
  y:             number     // UTM Northing (EPSG:25830)
  lng:           number     // WGS84 longitude
  lat:           number     // WGS84 latitude
  parcela:       number
  subproyecto:   string
  powerStation:  string
  ncu:           number
  orden:         number     // Tracker order within parcela
  strings:       string     // e.g. '2S'
  numHincas:     number     // total piles in tracker (9)
  posicion:      number     // pile position 1-9
  esInicial:     boolean
  esFinal:       boolean
  esMotora:      boolean
  tieneDamper:   boolean
  numDampers:    number
  layerHinca:    string
  symbol:        string
  pierType:      PierType
  section:       string
  lengthMm:      number
  partNo:        string
  color:         PierColor
  // Installation state (set by user)
  overallStatus: TrackerStatus
  steps:         PileSteps
}

// ---------------------------------------------------------------------------
// Aggregated tracker (9 piles → 1 tracker)
// ---------------------------------------------------------------------------
export interface TrackerData {
  codigoTracker: string
  parcela:       number
  subproyecto:   string
  powerStation:  string
  ncu:           number
  orden:         number     // Tracker_Orden_Parcela
  strings:       string
  numHincas:     number
  piles:         PileData[]
  // Computed geometry
  centerLng:     number
  centerLat:     number
  minLng:        number
  maxLng:        number
  minLat:        number
  maxLat:        number
  // Status (from localStorage / Supabase)
  status:        TrackerStatus
  progressPct:   number
}

// ---------------------------------------------------------------------------
// Parcela summary
// ---------------------------------------------------------------------------
export interface ParcelaStats {
  parcela:      number
  totalTrackers: number
  noIniciada:   number
  enCurso:      number
  finalizada:   number
  bloqueada:    number
  progressPct:  number   // weighted average
  bounds:       [number, number, number, number]  // [minLng, minLat, maxLng, maxLat]
}

// ---------------------------------------------------------------------------
// Full parsed plant dataset
// ---------------------------------------------------------------------------
export interface PlantDataset {
  trackersByParcela: Map<number, TrackerData[]>
  parcelaStats:      Map<number, ParcelaStats>
  totalTrackers:     number
  totalPiles:        number
  bounds:            [number, number, number, number]
}

// ---------------------------------------------------------------------------
// Layer visibility state
// ---------------------------------------------------------------------------
export interface PlantLayersState {
  trackerLines: boolean
  pilePoints:   boolean
  motorPiers:   boolean
  labels:       boolean
}

// ---------------------------------------------------------------------------
// Status display metadata
// ---------------------------------------------------------------------------
export const TRACKER_STATUS_META: Record<TrackerStatus, {
  label:    string
  color:    string
  glowColor: string
  bgClass:  string
  textClass: string
}> = {
  NO_INICIADA: {
    label:     'No iniciada',
    color:     '#475569',
    glowColor: '#64748B',
    bgClass:   'bg-slate-600',
    textClass: 'text-slate-300',
  },
  EN_CURSO: {
    label:     'En curso',
    color:     '#F59E0B',
    glowColor: '#FCD34D',
    bgClass:   'bg-amber-500',
    textClass: 'text-amber-300',
  },
  FINALIZADA: {
    label:     'Finalizada',
    color:     '#22C55E',
    glowColor: '#4ADE80',
    bgClass:   'bg-green-500',
    textClass: 'text-green-300',
  },
  BLOQUEADA: {
    label:     'Bloqueada',
    color:     '#EF4444',
    glowColor: '#F87171',
    bgClass:   'bg-red-500',
    textClass: 'text-red-300',
  },
}

// ---------------------------------------------------------------------------
// Installation steps from Actividades Obra 2.xlsx
// Each step: cumulative progressPct = sum of weights of all PREVIOUS steps / total
// ---------------------------------------------------------------------------

// Tracker steps (WBS sección 3 — "Estados de Tracker" según Actividades Obra 2.xlsx)
// 8 pasos, pesos suman exactamente 1.00
export const TRACKER_STEPS = [
  { code: '3.1', label: 'Descarga y reparto estructura', shortLabel: 'Descarga',  weight: 0.150, color: '#64748B', glowColor: '#94A3B8' },
  { code: '3.2', label: 'Montaje de Motora',             shortLabel: 'Motora',    weight: 0.083, color: '#3B82F6', glowColor: '#93C5FD' },
  { code: '3.3', label: 'Montaje de BHA',                shortLabel: 'BHA',       weight: 0.084, color: '#8B5CF6', glowColor: '#C4B5FD' },
  { code: '3.4', label: 'Montaje de Torque Tubo',        shortLabel: 'T.Tubo',    weight: 0.100, color: '#6366F1', glowColor: '#818CF8' },
  { code: '3.5', label: 'Montaje de damper',             shortLabel: 'Damper',    weight: 0.083, color: '#0891B2', glowColor: '#22D3EE' },
  { code: '3.6', label: 'Montaje de NCU',                shortLabel: 'NCU',       weight: 0.050, color: '#06B6D4', glowColor: '#67E8F9' },
  { code: '3.7', label: 'Remachado de tracker',          shortLabel: 'Remachado', weight: 0.300, color: '#F59E0B', glowColor: '#FCD34D' },
  { code: '3.8', label: 'Montaje de module rails',       shortLabel: 'M.Rails',   weight: 0.150, color: '#22C55E', glowColor: '#4ADE80' },
] as const
export type TrackerStepCode = typeof TRACKER_STEPS[number]['code']

/** Returns the progressPct at the START of a given tracker step (0–100). */
export function trackerStepStartPct(stepCode: TrackerStepCode): number {
  const idx = TRACKER_STEPS.findIndex(s => s.code === stepCode)
  if (idx <= 0) return 0
  const total = TRACKER_STEPS.reduce((s, st) => s + st.weight, 0)
  const done  = TRACKER_STEPS.slice(0, idx).reduce((s, st) => s + st.weight, 0)
  return Math.round((done / total) * 100)
}

/** Returns which step index (0-based) is active given status + progressPct.
 *  Returns -1 if FINALIZADA (all done), null if NO_INICIADA. */
export function activeTrackerStepIdx(status: TrackerStatus, pct: number): number | null {
  if (status === 'NO_INICIADA') return null
  if (status === 'FINALIZADA')  return -1
  const total      = TRACKER_STEPS.reduce((s, st) => s + st.weight, 0)
  const thresholds = TRACKER_STEPS.map((_, i) => {
    const done = TRACKER_STEPS.slice(0, i).reduce((s, st) => s + st.weight, 0)
    return Math.round((done / total) * 100)
  })
  let active = 0
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (pct >= thresholds[i]) { active = i; break }
  }
  return active
}

// Pile steps — tipo único, 6 pasos (WBS sección 2 — "Estados de Hinca")
// esMotora y tieneDamper son propiedades visuales (color del punto), no tipos distintos.
// Pesos suman exactamente 1.00
export const PILE_STEPS = [
  { code: '2.1', label: 'Marcado topográfico',  shortLabel: 'Marcado',   weight: 0.02, color: '#475569', glowColor: '#94A3B8' },
  { code: '2.2', label: 'Perforación',           shortLabel: 'Perfor.',   weight: 0.35, color: '#78716C', glowColor: '#A8A29E' },
  { code: '2.3', label: 'Relleno drilling',      shortLabel: 'Relleno',   weight: 0.03, color: '#92400E', glowColor: '#D97706' },
  { code: '2.4', label: 'Descarga hincas',       shortLabel: 'Descarga',  weight: 0.05, color: '#0891B2', glowColor: '#22D3EE' },
  { code: '2.5', label: 'Marcado altura libre',  shortLabel: 'Marc.H',    weight: 0.03, color: '#6366F1', glowColor: '#818CF8' },
  { code: '2.6', label: 'Hincado',               shortLabel: 'Hincado',   weight: 0.52, color: '#F59E0B', glowColor: '#FCD34D' },
] as const
export type PileStepCode = typeof PILE_STEPS[number]['code']
export type PileStepRecord = Partial<Record<PileStepCode, PileStepState>>

/** @deprecated Usar PileStepRecord. Mantenido para compatibilidad con localStorage. */
export type PileSteps = PileStepRecord

// Vallado steps (WBS CAT 18 — "Estados de Vallado")
export const VALLADO_STEPS = [
  { code: '18.1', label: 'Apertura de hoyos',   shortLabel: 'Hoyos',    weight: 0.15, color: '#78716C', glowColor: '#A8A29E' },
  { code: '18.2', label: 'Hormigonado postes',  shortLabel: 'Hormigón', weight: 0.35, color: '#92400E', glowColor: '#D97706' },
  { code: '18.3', label: 'Tendido de malla',    shortLabel: 'Malla',    weight: 0.45, color: '#F59E0B', glowColor: '#FCD34D' },
  { code: '18.4', label: 'Instalación puertas', shortLabel: 'Puertas',  weight: 0.05, color: '#22C55E', glowColor: '#4ADE80' },
] as const

// Zanja BT steps (WBS CAT 7)
export const ZANJA_BT_STEPS = [
  { code: '7.1', label: 'Apertura de zanja BT',     shortLabel: 'Apertura', weight: 0.40, color: '#78716C', glowColor: '#A8A29E' },
  { code: '7.2', label: 'Tendido de cable/tubo BT', shortLabel: 'Tendido',  weight: 0.20, color: '#F59E0B', glowColor: '#FCD34D' },
  { code: '7.3', label: 'Tapado de zanja BT',       shortLabel: 'Tapado',   weight: 0.40, color: '#22C55E', glowColor: '#4ADE80' },
] as const

// Vial steps (WBS CAT 19)
export const VIAL_STEPS = [
  { code: '19.1', label: 'Replanteo',                            shortLabel: 'Replanteo', weight: 0.03, color: '#64748B', glowColor: '#94A3B8' },
  { code: '19.2', label: 'Desbroce y retirada tierra vegetal',   shortLabel: 'Desbroce',  weight: 0.15, color: '#78716C', glowColor: '#A8A29E' },
  { code: '19.3', label: 'Terraplenado con material competente', shortLabel: 'Terrapl.',  weight: 0.25, color: '#92400E', glowColor: '#D97706' },
  { code: '19.4', label: 'Conformación de subrasante',           shortLabel: 'Subras.',   weight: 0.32, color: '#F59E0B', glowColor: '#FCD34D' },
  { code: '19.5', label: 'Drenajes',                             shortLabel: 'Drenajes',  weight: 0.15, color: '#0891B2', glowColor: '#22D3EE' },
  { code: '19.6', label: 'Ejecución de firme hasta coronación',  shortLabel: 'Firme',     weight: 0.10, color: '#22C55E', glowColor: '#4ADE80' },
] as const

// Inversor string steps (WBS CAT 12)
export const INVERSOR_STRING_STEPS = [
  { code: '12.1', label: 'Instalación inversores string',     shortLabel: 'Inst.',   weight: 0.20, color: '#64748B', glowColor: '#94A3B8' },
  { code: '12.2', label: 'Conexión inversores AC',            shortLabel: 'AC',      weight: 0.25, color: '#6366F1', glowColor: '#818CF8' },
  { code: '12.3', label: 'Conexión inversores CC',            shortLabel: 'CC',      weight: 0.35, color: '#F59E0B', glowColor: '#FCD34D' },
  { code: '12.4', label: 'Pruebas BT, megados y polaridades', shortLabel: 'Pruebas', weight: 0.10, color: '#0891B2', glowColor: '#22D3EE' },
  { code: '12.5', label: 'Etiquetado',                        shortLabel: 'Etiq.',   weight: 0.05, color: '#78716C', glowColor: '#A8A29E' },
  { code: '12.6', label: 'Puesta en marcha y comisionado',    shortLabel: 'COM',     weight: 0.05, color: '#22C55E', glowColor: '#4ADE80' },
] as const

// Subestacion steps (WBS CAT 20 — 13 estados)
// ⚠️ El codigo '20.10' debe almacenarse siempre como string, nunca como numero
export const SUBESTACION_STEPS = [
  { code: '20.1',  label: 'Desbroce y retirada tierra vegetal',        shortLabel: 'Desbroce',  weight: 0.020, color: '#78716C', glowColor: '#A8A29E' },
  { code: '20.2',  label: 'Replanteo',                                  shortLabel: 'Replanteo', weight: 0.010, color: '#64748B', glowColor: '#94A3B8' },
  { code: '20.3',  label: 'Ejecución de plataforma',                    shortLabel: 'Plataforma',weight: 0.100, color: '#92400E', glowColor: '#D97706' },
  { code: '20.4',  label: 'Red de puesta a tierra, malla enterrada',    shortLabel: 'PAT',       weight: 0.020, color: '#78716C', glowColor: '#A8A29E' },
  { code: '20.5',  label: 'Cimentación trafos principales',             shortLabel: 'Cim.Trafo', weight: 0.080, color: '#92400E', glowColor: '#D97706' },
  { code: '20.6',  label: 'Zapatas, losas y resto de bancadas',         shortLabel: 'Bancadas',  weight: 0.050, color: '#92400E', glowColor: '#D97706' },
  { code: '20.7',  label: 'Instalación edificios',                      shortLabel: 'Edificios', weight: 0.080, color: '#6366F1', glowColor: '#818CF8' },
  { code: '20.8',  label: 'Canalizaciones y drenajes',                  shortLabel: 'Canal.',    weight: 0.100, color: '#0891B2', glowColor: '#22D3EE' },
  { code: '20.9',  label: 'Izado de estructura',                        shortLabel: 'Izado',     weight: 0.130, color: '#8B5CF6', glowColor: '#C4B5FD' },
  { code: '20.10', label: 'Montaje de equipos',                         shortLabel: 'Equipos',   weight: 0.300, color: '#F59E0B', glowColor: '#FCD34D' },
  { code: '20.11', label: 'Cableado y conexionado',                     shortLabel: 'Cableado',  weight: 0.100, color: '#3B82F6', glowColor: '#93C5FD' },
  { code: '20.12', label: 'Vallado subestación',                        shortLabel: 'Vallado',   weight: 0.005, color: '#78716C', glowColor: '#A8A29E' },
  { code: '20.13', label: 'Pruebas',                                    shortLabel: 'Pruebas',   weight: 0.005, color: '#22C55E', glowColor: '#4ADE80' },
] as const

export const PILE_STEP_LABELS: Record<PileStepCode, string> = {
  '2.1': 'Marcado topográfico',
  '2.2': 'Perforación',
  '2.3': 'Relleno drilling',
  '2.4': 'Descarga hincas',
  '2.5': 'Marcado altura libre',
  '2.6': 'Hincado',
}

// ---------------------------------------------------------------------------
// Pier type colors (for pile points on map)
// ---------------------------------------------------------------------------
export const PIER_TYPE_COLORS: Record<PierType, string> = {
  HAP:     '#E2E8F0',   // white-ish (BLANCO)
  SAP:     '#94A3B8',   // gray (GRIS)
  SAP_EDGE:'#60A5FA',   // blue (AZUL)
  SAP_END: '#34D399',   // teal
  HMP:     '#FBBF24',   // yellow (AMARILLO) — Heavy Motor Pier
  SMP:     '#4ADE80',   // green (VERDE) — Standard Motor Pier
}

// =============================================================================
// PART NUMBER CATALOG — Reference table for pile types
// =============================================================================

export const PART_NO_CONFIG = {
  '73135': {
    descripcion: 'Array Pier W6x7',
    colorLabel:  'SIN COLOR',
    pierType:    'SAP' as PierType,
    hex:         '#64748B',
    glowHex:     '#94A3B8',
    esMotora:    false,
  },
  '73186': {
    descripcion: 'Array Pier W6x8.5',
    colorLabel:  'AZUL',
    pierType:    'SAP_EDGE' as PierType,
    hex:         '#3B82F6',
    glowHex:     '#93C5FD',
    esMotora:    false,
  },
  '73340': {
    descripcion: 'Array Pier W6x15',
    colorLabel:  'BLANCO',
    pierType:    'HAP' as PierType,
    hex:         '#E2E8F0',
    glowHex:     '#F8FAFC',
    esMotora:    false,
  },
  '71957': {
    descripcion: 'Motor Pier W6x15',
    colorLabel:  'VERDE',
    pierType:    'SMP' as PierType,
    hex:         '#22C55E',
    glowHex:     '#86EFAC',
    esMotora:    true,
  },
  '71961': {
    descripcion: 'Motor Pier W6x15',
    colorLabel:  'AMARILLO',
    pierType:    'HMP' as PierType,
    hex:         '#EAB308',
    glowHex:     '#FDE047',
    esMotora:    true,
  },
} as const

export type PartNoKey = keyof typeof PART_NO_CONFIG

export const WBS_ACTIVITIES = {
  '1':  { label: 'Instalaciones temporales',    elementType: 'global' },
  '2':  { label: 'Hincas',                      elementType: 'hinca' },
  '3':  { label: 'Tracker',                     elementType: 'tracker' },
  '4':  { label: 'Módulos',                     elementType: 'tracker' },
  '5':  { label: 'Zanjas BT',                   elementType: 'zanja_bt' },
  '6':  { label: 'Zanjas MT',                   elementType: 'zanja_mt' },
  '7':  { label: 'Inversores Combiner Boxes',   elementType: 'combiner' },
  '8':  { label: 'Inversores String',           elementType: 'inversor' },
  '9':  { label: 'Power Station',               elementType: 'power_station' },
  '10': { label: 'Estaciones Meteorológicas',   elementType: 'meteo' },
  '11': { label: 'Subestaciones',               elementType: 'subestacion' },
  '12': { label: 'Línea 132 kV',               elementType: 'linea_132' },
  '13': { label: 'Línea 400kV',                elementType: 'linea_400' },
  '14': { label: 'Cámaras empalme y botellas', elementType: 'camara' },
  '15': { label: 'CCTV',                        elementType: 'cctv' },
  '16': { label: 'Vallado',                     elementType: 'vallado' },
  '17': { label: 'Viales',                      elementType: 'vial' },
  '18': { label: 'Urbanización',               elementType: 'urbanizacion' },
  '19': { label: 'Edificios',                   elementType: 'edificio' },
  '20': { label: 'Almacén',                    elementType: 'almacen' },
} as const
